import { StockStatusEnum } from '@petsmart-ui/sparky';

import { DeliveryType } from '@libs/core';

import { validateStockStatus } from '../../../utils/stockStatusUtils';

export type FulfillmentMethod = 'bopis' | 'sdd' | 'sth';
export type FulfillmentStatusTone = 'success' | 'error';

export type FulfillmentStatus = Readonly<{
    message: string;
    tone?: FulfillmentStatusTone;
}>;

type FulfillmentConfigurationInput = Readonly<{
    fulfillmentMethod: FulfillmentMethod;
    hasBopisStore: boolean;
    hasSddValidation: boolean;
}>;

type StockStatusInput = Readonly<{
    availableStock: number;
    minimumCaseQty: number;
}>;

const FULFILLMENT_STATUS = {
    CHECK_AVAILABILITY: { message: 'Check availability' },
    IN_STOCK: { message: 'In stock', tone: 'success' },
    LOW_STOCK: { message: 'Only a few left', tone: 'error' },
    NEARBY_STORES_AVAILABLE: { message: 'Nearby stores with availability', tone: 'success' },
    NOT_AVAILABLE_IN_AREA: { message: 'Not available in your area', tone: 'error' },
    OUT_OF_STOCK: { message: 'Out of stock', tone: 'error' },
    READY_AFTER_VET_APPROVAL: { message: 'Most orders ready within 2 hours after vet approval', tone: 'success' },
    READY_WITHIN_TWO_HOURS: { message: 'Most orders ready within 2 hours', tone: 'success' },
} satisfies Record<string, FulfillmentStatus>;

const FULFILLMENT_DEFAULT_METHOD_ORDER: readonly FulfillmentMethod[] = ['bopis', 'sth', 'sdd'];

export function getFulfillmentConfigurationError({
    fulfillmentMethod,
    hasBopisStore,
    hasSddValidation,
}: FulfillmentConfigurationInput): string | undefined {
    if (fulfillmentMethod === 'bopis' && !hasBopisStore) {
        return 'Store ID is required for BOPIS';
    }

    if (fulfillmentMethod === 'sdd' && !hasSddValidation) {
        return 'DoorDash validation failed';
    }
}

export function getBopisFulfillmentStatus({
    availableStock,
    minimumCaseQty,
    altAvailableStock,
    isStoreSelected,
    isPharmacy,
}: StockStatusInput & {
    altAvailableStock: number;
    isStoreSelected: boolean;
    isPharmacy: boolean;
}): FulfillmentStatus {
    if (!isStoreSelected) {
        return FULFILLMENT_STATUS.READY_WITHIN_TWO_HOURS;
    }

    const stockStatus = validateStockStatus({ stockQuantity: availableStock, minimumCaseQty });
    const altStockStatus = validateStockStatus({ stockQuantity: altAvailableStock, minimumCaseQty });

    if (stockStatus === StockStatusEnum.OutOfStock) {
        return altStockStatus === StockStatusEnum.InStock || altStockStatus === StockStatusEnum.FewLeftInStock
            ? FULFILLMENT_STATUS.NEARBY_STORES_AVAILABLE
            : FULFILLMENT_STATUS.OUT_OF_STOCK;
    }

    if (stockStatus === StockStatusEnum.FewLeftInStock) {
        return FULFILLMENT_STATUS.LOW_STOCK;
    }

    return isPharmacy ? FULFILLMENT_STATUS.READY_AFTER_VET_APPROVAL : FULFILLMENT_STATUS.READY_WITHIN_TWO_HOURS;
}

export function getSddFulfillmentStatus({
    availableStock,
    minimumCaseQty,
    isAvailable,
    hasStreetAddress,
}: StockStatusInput & {
    isAvailable: boolean;
    hasStreetAddress: boolean;
}): FulfillmentStatus | undefined {
    if (!hasStreetAddress) {
        return FULFILLMENT_STATUS.CHECK_AVAILABILITY;
    }

    if (!isAvailable) {
        return FULFILLMENT_STATUS.NOT_AVAILABLE_IN_AREA;
    }

    const stockStatus = validateStockStatus({ stockQuantity: availableStock, minimumCaseQty });

    if (stockStatus === StockStatusEnum.InStock) {
        return FULFILLMENT_STATUS.IN_STOCK;
    }

    if (stockStatus === StockStatusEnum.FewLeftInStock) {
        return FULFILLMENT_STATUS.LOW_STOCK;
    }

    if (stockStatus === StockStatusEnum.OutOfStock) {
        return FULFILLMENT_STATUS.OUT_OF_STOCK;
    }

    return undefined;
}

export function getSthFulfillmentStatus({
    availableStock,
    minimumCaseQty,
    isPharmacyOutOfStock,
}: StockStatusInput & {
    isPharmacyOutOfStock: boolean;
}): FulfillmentStatus {
    const stockStatus = validateStockStatus({
        stockQuantity: availableStock,
        minimumCaseQty,
        customPharmacyOOS: isPharmacyOutOfStock,
    });

    if (stockStatus === StockStatusEnum.OutOfStock) {
        return FULFILLMENT_STATUS.OUT_OF_STOCK;
    }

    if (stockStatus === StockStatusEnum.FewLeftInStock) {
        return FULFILLMENT_STATUS.LOW_STOCK;
    }

    return FULFILLMENT_STATUS.IN_STOCK;
}

export function resolveFulfillmentMethod({
    availableMethods,
    selectedMethod,
}: Readonly<{
    availableMethods: Partial<Record<FulfillmentMethod, boolean>>;
    selectedMethod?: FulfillmentMethod;
}>): FulfillmentMethod {
    if (selectedMethod && availableMethods[selectedMethod]) {
        return selectedMethod;
    }

    return FULFILLMENT_DEFAULT_METHOD_ORDER.find(method => availableMethods[method]) ?? DeliveryType.BOPIS;
}
